/**
 * Shared cache for the slim user list (GET /api/v1/users/summary).
 *
 * Why a store: prior to this, every component that called `useUsers()` issued
 * its own HTTP request. Opening a single customer drawer fired the list ~5
 * times, each ~11MB. This store fans those concurrent calls down to a single
 * in-flight request and caches the result for CACHE_TTL_MS.
 *
 * It deliberately does NOT use `persist` — auth state lives in memory only
 * after logout, and the slim list is cheap to refetch on next login.
 */

import { create } from 'zustand'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'

export interface UserSummary {
  userId: string
  email: string
  name: string
  /**
   * The LEGACY four-value enum — ADMIN | MANAGER | EXECUTIVE | VIEWER.
   *
   * Almost useless as an identity: nine of thirteen users on testco carry `EXECUTIVE` while
   * pointing at BDE, VIEWER or Finance Officer. Anything that needs to know what somebody
   * actually is must read [roleId]. Kept because older screens still switch on it.
   */
  role: string
  /**
   * The user's REAL role — the id an approval chain, a permission binding and a scope all
   * address.
   *
   * **The server has sent this all along** (`UserSummaryResponse.roleId`, added in RBAC step 4);
   * this interface simply did not declare it, so every consumer of the shared store was blind to
   * it and the onboarding chain could name a role but never the people in it. Optional because
   * a user predating the roleId backfill can still have none.
   */
  roleId?: string | null
  /** The role's display name, resolved server-side. Falls back to the legacy enum's name. */
  roleName?: string | null
  isActive: boolean
}

interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  timestamp?: string
}

interface UsersSummaryState {
  users: UserSummary[]
  isLoading: boolean
  error: string | null
  /** When the cache was last populated. null means never. */
  lastFetchedAt: number | null
  /** Shared in-flight promise so concurrent callers dedupe to one request. */
  inFlight: Promise<UserSummary[]> | null

  /**
   * Fetch (or return cached) summary list. `force` bypasses TTL.
   *
   * The list is always everyone, deactivated people included, whatever `activeOnly` says: one
   * cached list serves every caller, so it has to be the widest one. It used to be whichever
   * list the first caller asked for, so a screen naming someone who has left (a task's holder, a
   * step's actor) got the active-only list whenever a picker had loaded first, and showed
   * "Unknown". `useUsers(activeOnly)` narrows it for its callers; `activeOnly` stays in the
   * signature for them.
   */
  fetch: (token: string, activeOnly: boolean, force?: boolean) => Promise<UserSummary[]>
  /** Drop all cached state. Called on logout. */
  reset: () => void
}

const CACHE_TTL_MS = 5 * 60 * 1000 // 5 minutes

export const useUsersSummaryStore = create<UsersSummaryState>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,
  lastFetchedAt: null,
  inFlight: null,

  fetch: async (token, _activeOnly, force = false) => {
    const state = get()

    if (state.inFlight) return state.inFlight

    const isFresh =
      !force &&
      state.lastFetchedAt !== null &&
      Date.now() - state.lastFetchedAt < CACHE_TTL_MS

    if (isFresh) return state.users

    const request = (async () => {
      set({ isLoading: true, error: null })
      try {
        const response = await authFetch<ApiResponse<UserSummary[]>>(
          '/api/v1/users/summary',
          token
        )
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch users')
        }
        const users = response.data ?? []
        set({
          users,
          isLoading: false,
          error: null,
          lastFetchedAt: Date.now(),
          inFlight: null,
        })
        return users
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load users'
        set({ isLoading: false, error: message, inFlight: null })
        throw err
      }
    })()

    set({ inFlight: request })
    return request
  },

  reset: () =>
    set({
      users: [],
      isLoading: false,
      error: null,
      lastFetchedAt: null,
      inFlight: null,
    }),
}))

// Drop cached users whenever the auth token clears (logout / tenant switch).
useAuthStore.subscribe((state, prev) => {
  if (prev.tokens?.accessToken && !state.tokens?.accessToken) {
    useUsersSummaryStore.getState().reset()
  }
})
